import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector,
} from '@ngrx/store';
import {
  selectCurrentRoute,
  selectRouteParams,
  selectUrl,
} from '../../router.selectors';
import * as fromCatalog from './catalog.reducer';

function catalogIdsAndParamPresent(
  catalogIds: string[],
  catalogId: string,
): boolean {
  return !!catalogIds && catalogIds.length > 0 && !!catalogId;
}

export const selectCatalogState =
  createFeatureSelector<fromCatalog.CatalogState>(
    fromCatalog.catalogFeatureKey,
  );

export const selectCatalogIdsLoading: MemoizedSelector<object, boolean> =
  createSelector(selectCatalogState, fromCatalog.getLoading);

export const selectCatalogIds = createSelector(
  selectCatalogState,
  fromCatalog.getCatalogIds,
);

export const selectTotalCatalogs = createSelector(
  selectCatalogIds,
  (catalogIds: string[]) => catalogIds?.length || 0,
);

export const selectFirstCatalogId = createSelector(
  selectCatalogIds,
  selectCatalogIdsLoading,
  (catalogIds: string[], loading: boolean) => {
    if (catalogIds === undefined || loading) return;

    return catalogIds.length > 0 ? catalogIds[0] : null;
  },
);

export const selectNextRouteFragment = createSelector(
  selectCatalogIds,
  selectRouteParams,
  (catalogIds: string[], params) => {
    if (!catalogIdsAndParamPresent(catalogIds, params?.catalogId))
      return undefined;
    const index = catalogIds.findIndex(
      c => c.toLowerCase() === params.catalogId.toLowerCase(),
    );
    const next = index + 1;
    return index >= 0 && next < catalogIds.length
      ? `commerce/catalog/${catalogIds[next]}`
      : 'commerce/address';
  },
);

export const selectPreviousRouteFragment = createSelector(
  selectCatalogIds,
  selectRouteParams,
  selectCurrentRoute,
  selectUrl,
  (catalogIds: string[], params, currentRoute, url) => {
    const isAddressPage = url?.includes('address');
    const isSuggestedAddressPage =
      currentRoute?.routeConfig?.path?.includes('suggested-address');
    const isFailedAddonsPage =
      currentRoute?.routeConfig?.path?.includes('failed-add-ons');
    if (
      !isAddressPage &&
      !catalogIdsAndParamPresent(catalogIds, params?.catalogId) &&
      !isFailedAddonsPage
    ) {
      return undefined;
    }
    const index = catalogIds.findIndex(
      c => c.toLowerCase() === params?.catalogId?.toLowerCase(),
    );
    let prev: number = index - 1;
    if (isAddressPage || isFailedAddonsPage) {
      prev = catalogIds.length - 1;
    }
    if (isSuggestedAddressPage) {
      return 'commerce/address';
    }
    return prev >= 0 ? `commerce/catalog/${catalogIds[prev]}` : undefined;
  },
);

export const selectCurrentCatalogId = createSelector(
  selectCatalogIds,
  selectRouteParams,
  (catalogIds: string[], params) => {
    return catalogIdsAndParamPresent(catalogIds, params?.catalogId)
      ? catalogIds.find(
          c => !!c && c.toLowerCase() === params?.catalogId.toLowerCase(),
        )
      : undefined;
  },
);

export const selectCurrentCatalogPageNumber = createSelector(
  selectCatalogIds,
  selectCurrentCatalogId,
  (catalogIds: string[], currentCatalogId: string | undefined) => {
    return catalogIds && currentCatalogId
      ? catalogIds.findIndex(c => c === currentCatalogId) + 1
      : catalogIds && !currentCatalogId
        ? catalogIds.length
        : 0;
  },
);

export const selectIsFinalCatalogPage = createSelector(
  selectCatalogIds,
  selectCurrentCatalogId,
  (catalogIds: string[], currentCatalogId: string | undefined) => {
    return (
      catalogIds !== undefined &&
      currentCatalogId !== undefined &&
      catalogIds[catalogIds.length - 1] === currentCatalogId
    );
  },
);
